import {
  ArrowDown,
  CheckCircle2,
  Coins,
  Crown,
  Droplet,
  Landmark,
  Lock,
  RefreshCw,
  Sparkle,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import type { ProjectedMatrixCycle } from '../lib/lookup-types';

/** Coarse colour bucket for a redirect-pattern event kind. */
export function positionKindTone(kind: string): 'downline' | 'income' | 'treasury' | 'recycle' | 'empty' {
  switch (kind) {
    case 'sponsor_one':
    case 'sponsor_two':
    case 'downline_route':
      return 'downline';
    case 'slot_owner':
      return 'income';
    case 'upgrade_hold':
    case 'upgrade_complete':
    case 'token_liquidity':
    case 'leaders_club':
    case 'treasury_fallback':
      return 'treasury';
    case 'recycle':
      return 'recycle';
    case 'forfeited':
    default:
      return 'empty';
  }
}

/** A finer-grained icon per event kind, layered on top of the coarser colour bucket. */
export function positionKindIcon(kind: string): LucideIcon {
  switch (kind) {
    case 'sponsor_one':
      return UserPlus;
    case 'sponsor_two':
      return Users;
    case 'downline_route':
      return ArrowDown;
    case 'slot_owner':
      return Coins;
    case 'token_liquidity':
      return Droplet;
    case 'leaders_club':
      return Crown;
    case 'upgrade_hold':
      return Lock;
    case 'upgrade_complete':
      return CheckCircle2;
    case 'recycle':
      return RefreshCw;
    case 'treasury_fallback':
      return Landmark;
    default:
      return Sparkle;
  }
}

export const POSITION_KIND_LABEL: Record<string, string> = {
  sponsor_one: 'Sponsor-1 routing',
  sponsor_two: 'Sponsor-2 routing',
  slot_owner: 'Slot owner',
  upgrade_hold: 'Upgrade holding',
  forfeited: 'Forfeited holding (dormancy) · blank again',
  upgrade_complete: 'Upgrade complete · next slot',
  token_liquidity: 'Token liquidity',
  leaders_club: 'Leaders club',
  downline_route: 'Downline routing',
  recycle: 'Recycle + recycle payment routing',
  treasury_fallback: 'Treasury (no sponsor)',
  open: 'Open',
};

/**
 * Short text shown on the chip face. Every one of the twelve positions seats a member under the
 * owner's locked payment-destination matrix, so a chip without an occupant simply has nobody in
 * it yet — the destination label lives in the tooltip and the legend, not on the face.
 */
function chipFace(_kind: string, occupantId: string | null): string {
  // Always the whole ID: test IDs are BD-1 onward, live IDs will be at least six digits, and a
  // trimmed tail ("3456") would be ambiguous between BD-123456 and BD-983456.
  if (occupantId) return occupantId;
  return 'Open';
}

export const POSITION_LEGEND: { kind: string; label: string }[] = [
  { kind: 'sponsor_one', label: 'Sponsor-1 routing' },
  { kind: 'sponsor_two', label: 'Sponsor-2 routing' },
  { kind: 'slot_owner', label: 'Slot owner' },
  { kind: 'upgrade_hold', label: 'Upgrade holding' },
  { kind: 'upgrade_complete', label: 'Upgrade complete' },
  { kind: 'token_liquidity', label: 'Token liquidity' },
  { kind: 'leaders_club', label: 'Leaders club' },
  { kind: 'downline_route', label: 'Downline routing' },
  { kind: 'recycle', label: 'Recycle + payment routing' },
  { kind: 'treasury_fallback', label: 'Treasury' },
  { kind: 'forfeited', label: 'Forfeited holding (dormancy)' },
  { kind: 'open', label: 'Open' },
];

export function PositionLegend() {
  return (
    <div className="position-legend">
      {POSITION_LEGEND.map(({ kind, label }) => {
        const Icon = positionKindIcon(kind);
        return (
          <span key={kind} className={`position-legend-item kind-${positionKindTone(kind)}${kind === 'open' ? ' empty' : ''}`}>
            <Icon size={12} /> {label}
          </span>
        );
      })}
    </div>
  );
}

/**
 * The full twelve positions of one cycle as a compact icon grid — the same presentation the admin
 * console uses, so a member's slot reads identically wherever it appears.
 */
export function PositionGrid({
  cycle,
  columns = 4,
  positions,
}: {
  cycle: ProjectedMatrixCycle;
  columns?: number;
  /** Admin position filter (rules/rule7.txt addendum C): render only these positions. */
  positions?: number[] | null | undefined;
}) {
  const shown = positions && positions.length > 0 ? positions : Array.from({ length: 12 }, (_, index) => index + 1);
  return (
    <div className="position-mini-grid" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {shown.map((position) => {
        // A position can carry more than one event (e.g. a root's P1 pass-up also routes to
        // Treasury) — prefer the occupant-facing one for the chip's face.
        const entries = cycle.positions.filter((item) => item.position === position);
        // A forfeited holding (rule 34) is history: the live seat or the open position wins.
        const primary = entries.find((item) => item.occupantId && item.kind !== 'forfeited') ?? entries.find((item) => item.kind === 'open') ?? entries[0];
        const kind = primary?.kind ?? 'open';
        const occupant = primary?.occupantId ?? null;
        const filled = Boolean(occupant);
        const Icon = positionKindIcon(kind);
        const tooltip = entries.length > 0
          ? entries.map((entry) => `${entry.kind === 'downline_route' ? (cycle.cycleNumber >= 2 ? '2nd Downline routing' : '1st Downline routing') : POSITION_KIND_LABEL[entry.kind] ?? entry.kind}${entry.occupantId ? ` · ${entry.occupantId}` : ''}`).join(' + ')
          : 'Open';
        return (
          <div
            key={position}
            className={`position-chip kind-${positionKindTone(kind)}${filled ? '' : ' empty'}`}
            title={`P${position} · ${tooltip}`}
          >
            <span className="position-chip-icon"><Icon size={13} /></span>
            <small>P{position}</small>
            <strong className={occupant && occupant.length > 7 ? 'long' : undefined}>{chipFace(kind, occupant)}</strong>
          </div>
        );
      })}
    </div>
  );
}
